import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { testAppiumConnection } from '../../services/appiumService';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectAppiumUrl,
  selectAdbRunnerUrl,
  setAppiumUrl,
  setAdbRunnerUrl,
} from '../../redux/app/platformSlice';

export default function AppiumConnection() {
  const dispatch = useDispatch();
  const savedAppiumUrl = useSelector(selectAppiumUrl);
  const savedAdbRunnerUrl = useSelector(selectAdbRunnerUrl);
  const [appiumUrl, setLocalAppiumUrl] = useState(savedAppiumUrl);
  const [adbRunnerUrl, setLocalAdbRunnerUrl] = useState(savedAdbRunnerUrl);
  const [status, setStatus] = useState('disconnected');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (appiumUrl) checkConnection(appiumUrl);
  }, []);

  const checkConnection = async (u) => {
    if (!u) {
      setStatus('disconnected');
      return;
    }
    setStatus('connecting');
    const ok = await testAppiumConnection(u);
    setStatus(ok ? 'connected' : 'error');
  };

  const handleConnect = async () => {
    const normalized = appiumUrl.trim().replace(/\/$/, '');
    setLocalAppiumUrl(normalized);
    dispatch(setAppiumUrl(normalized));
    const normalizedAdb = adbRunnerUrl.trim().replace(/\/$/, '');
    setLocalAdbRunnerUrl(normalizedAdb);
    dispatch(setAdbRunnerUrl(normalizedAdb));
    await checkConnection(normalized);
    setIsEditing(false);
  };

  const handleDisconnect = () => {
    setLocalAppiumUrl('');
    setLocalAdbRunnerUrl('');
    dispatch(setAppiumUrl(''));
    dispatch(setAdbRunnerUrl(''));
    setStatus('disconnected');
  };

  const statusIcon = () => {
    if (status === 'connecting') return <CircularProgress size={16} />;
    if (status === 'connected')
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />;
    if (status === 'error') return <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />;
    return <LinkOffIcon sx={{ fontSize: 16 }} />;
  };

  if (status === 'connected' && !isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          icon={<CheckCircleIcon />}
          label="Appium"
          size="small"
          color="success"
          variant="outlined"
          onClick={() => setIsEditing(true)}
        />
        {adbRunnerUrl && (
          <Chip
            label="ADB Runner"
            size="small"
            color="info"
            variant="outlined"
            onClick={() => setIsEditing(true)}
          />
        )}
        <Tooltip title="Disconnect">
          <IconButton size="small" onClick={handleDisconnect}>
            <LinkOffIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        size="small"
        placeholder="http://localhost:4723"
        label="Appium URL"
        value={appiumUrl}
        onChange={(e) => setLocalAppiumUrl(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LinkIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: <InputAdornment position="end">{statusIcon()}</InputAdornment>,
        }}
        sx={{
          width: 260,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            fontSize: '0.85rem',
          },
        }}
      />

      <TextField
        size="small"
        placeholder="http://localhost:19090"
        label="ADB Runner URL"
        value={adbRunnerUrl}
        onChange={(e) => setLocalAdbRunnerUrl(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LinkIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          width: 240,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            fontSize: '0.85rem',
          },
        }}
      />

      <Tooltip title="Connect to Appium">
        <IconButton size="small" onClick={handleConnect} disabled={status === 'connecting'}>
          <LinkIcon />
        </IconButton>
      </Tooltip>

      {(status === 'error' || status === 'disconnected') && appiumUrl && (
        <Chip
          label={status === 'error' ? 'Connection failed' : 'Not connected'}
          size="small"
          color={status === 'error' ? 'error' : 'default'}
          variant="outlined"
        />
      )}
    </Box>
  );
}
